import { Form } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DEBOUNCE_DELAYS, FEE, PAYMENT_TYPE } from '@/consts/billing'
import type { Currency, FormValues } from '@/types/billing'
import { autoCorrectFee } from '@/utils/billing'
import { debounce } from '@/utils/debounce'

export function useRechargeForm() {
  const { t } = useTranslation('billing')
  const [form] = Form.useForm<FormValues>()

  const [feeFocus, setFeeFocus] = useState<'radio' | 'input' | null>('radio')
  const [inputValue, setInputValue] = useState<string>('')
  const [feePoint, setFeePoint] = useState<number>(FEE.DEFAULT)
  const [currency, setCurrency] = useState<Currency>('TWD')
  const [customFeeDescription, setCustomFeeDescription] = useState<string>(
    `ℹ ${t('rechargeModal.form.10xMsg')}`,
  )

  const initialValues = {
    fee: FEE.DEFAULT,
    currency: 'TWD' as Currency,
    type: PAYMENT_TYPE.NEWEBPAY,
  }

  const debouncedAutoCorrect = useMemo(
    () =>
      debounce((value: number) => {
        const roundedValue = autoCorrectFee(value)
        form.setFieldsValue({ customFee: roundedValue, fee: undefined })
        setFeePoint(roundedValue)
        setInputValue(roundedValue.toString())

        if (value !== roundedValue) {
          if (roundedValue === FEE.MAXIMUM) {
            setCustomFeeDescription(
              '✅ Automatically corrected to 99999990 (This field accepts only multiples of 10)',
            )
          } else {
            setCustomFeeDescription(
              `✅ ${t('rechargeModal.form.autoCorrectedMsg', { value: roundedValue })}`,
            )
          }
        } else {
          setCustomFeeDescription(`ℹ ${t('rechargeModal.form.10xMsg')}`)
        }
      }, DEBOUNCE_DELAYS.AUTO_CORRECT),
    [form, t],
  )

  const resetCustomFeeOnRadioFocus = useCallback(() => {
    if (feeFocus === 'radio') {
      setInputValue('')
      form.setFieldsValue({ customFee: undefined })
    }
  }, [feeFocus, form])

  return {
    form,
    feeFocus,
    setFeeFocus,
    inputValue,
    setInputValue,
    feePoint,
    setFeePoint,
    currency,
    setCurrency,
    customFeeDescription,
    setCustomFeeDescription,
    initialValues,
    debouncedAutoCorrect,
    resetCustomFeeOnRadioFocus,
  }
}
